import React, { useImperativeHandle, useCallback, useMemo, type KeyboardEvent } from 'react';
import { styled } from '@compiled/react';
import CommentIcon from '@atlaskit/icon/core/comment';
import CommentIconLegacy from '@atlaskit/icon/glyph/comment';
import { Box, Pressable, xcss } from '@atlaskit/primitives';
import { token } from '@atlaskit/tokens';
import { expVal } from '@atlassian/jira-feature-experiments';
import { fg } from '@atlassian/jira-feature-gating';
import { useIntl } from '@atlassian/jira-intl';
import type { IssueKey } from '@atlassian/jira-shared-types/src/general.tsx';
import { isVisualRefreshEnabled } from '@atlassian/jira-visual-refresh-rollout/src/feature-switch/index.tsx';
import type { CellNavigationProps } from '../../../common/types/cell-navigation.tsx';
import messages from './messages.tsx';

export type OnOpenAnalyticAttributes = {
	totalCount?: number;
	issueHasUnreadComment?: boolean;
	columnIndex?: number;
};

type Props = {
	issueKey: IssueKey;
	onClick: (issueId: string, analyticsAttributes?: OnOpenAnalyticAttributes) => void;
	// eslint-disable-next-line @typescript-eslint/no-invalid-void-type
	totalCount: number | void;
	hasUnread?: boolean;
	columnIndex?: number;
} & CellNavigationProps;

export const CommentsCell = ({
	issueKey,
	onClick,
	totalCount,
	cellRef,
	hasUnread,
	columnIndex,
}: Props) => {
	const { formatMessage } = useIntl();

	// Temporary attributes for Unread Comments in List View experiment
	// Clean up in https://hello.jira.atlassian.cloud/browse/WAR-1557
	const analyticAttributes = useMemo(() => {
		return fg('_jira_unread_comments_in_list_view_gate')
			? {
					totalCount: typeof totalCount === 'number' ? totalCount : undefined,
					columnIndex,
					...(expVal('jira_unread_comments_in_list_view', 'isEnabled', false)
						? { issueHasUnreadComment: hasUnread }
						: {}),
				}
			: undefined;
	}, [columnIndex, hasUnread, totalCount]);

	const handleClick = useCallback(() => {
		onClick(issueKey, analyticAttributes);
	}, [onClick, issueKey, analyticAttributes]);

	const handleKeyDownNew = useCallback(
		(event: KeyboardEvent<HTMLElement>) => {
			if (event.code === 'Enter') {
				onClick(issueKey, analyticAttributes);
				return true;
			}

			return false;
		},
		[analyticAttributes, issueKey, onClick],
	);

	const cellOverrides = useCallback(
		() => ({
			onKeyDown: handleKeyDownNew,
			onClick: () => true,
		}),
		[handleKeyDownNew],
	);
	useImperativeHandle(cellRef, cellOverrides, [cellOverrides]);

	const renderedCommentCount = useMemo(() => {
		if (totalCount == null) {
			return null;
		}
		// No comments, add comment
		if (totalCount === 0) {
			return formatMessage(messages.addComment);
		}
		// Less than 100 comments, show count
		if (totalCount < 100) {
			return formatMessage(messages.commentsCount, {
				commentCount: totalCount,
			});
		}
		// More than 99 comments, show 99+
		return formatMessage(messages.moreComments);
	}, [formatMessage, totalCount]);

	const cellContent = (
		<>
			<ContentWrapper noComments={totalCount === 0}>
				<CommentIcon
					color="currentColor"
					label={formatMessage(messages.comments)}
					LEGACY_size="medium"
					LEGACY_fallbackIcon={CommentIconLegacy}
				/>
				{renderedCommentCount}
			</ContentWrapper>
		</>
	);

	const newCellContent = (
		<Box
			xcss={[
				contentWrapperNewStyles,
				isVisualRefreshEnabled() && refreshedContentWrapperStyles,
				totalCount === 0 && noCommentsStyles,
			]}
		>
			<CommentIcon
				color="currentColor"
				label={formatMessage(messages.comments)}
				LEGACY_size="medium"
				LEGACY_fallbackIcon={CommentIconLegacy}
			/>
			{hasUnread && <UnreadIndicator />}
			{renderedCommentCount}
		</Box>
	);

	const content = expVal('jira_unread_comments_in_list_view', 'isEnabled', false)
		? newCellContent
		: cellContent;

	return fg('custom-interactive-elements-not-keyboard-focusable') ? (
		<Pressable xcss={wrapperStyles} onClick={handleClick}>
			{content}
		</Pressable>
	) : (
		// eslint-disable-next-line @atlassian/a11y/interactive-element-not-keyboard-focusable
		<Box xcss={wrapperStyles} onClick={handleClick}>
			{content}
		</Box>
	);
};

const UnreadIndicator = () => (
	<Box
		xcss={[unreadIndicatorStyles, isVisualRefreshEnabled() && refreshedIndicatorStyles]}
		testId="business-list.ui.list-view.comments-cell.unread"
	/>
);

// eslint-disable-next-line @atlaskit/ui-styling-standard/no-styled, @atlaskit/ui-styling-standard/no-exported-styles -- Ignored via go/DSP-18766
export const ContentWrapper = styled.div<{ noComments: boolean }>({
	display: 'flex',
	alignItems: 'center',
	height: '100%',
	// eslint-disable-next-line @atlaskit/ui-styling-standard/no-nested-selectors -- Ignored via go/DSP-18766
	'> span': {
		marginRight: token('space.050'),
	},
	// eslint-disable-next-line @atlaskit/ui-styling-standard/no-dynamic-styles -- Ignored via go/DSP-18766
	color: ({ noComments }) =>
		noComments ? token('color.text.subtlest') : token('color.text.accent.gray.bolder'),
});

const contentWrapperNewStyles = xcss({
	display: 'flex',
	alignItems: 'center',
	height: '100%',
	position: 'relative',
	gap: 'space.050',
	color: 'color.text.accent.gray.bolder',
});

const refreshedContentWrapperStyles = xcss({
	gap: 'space.100',
});

const noCommentsStyles = xcss({
	color: 'color.text.subtlest',
});

const wrapperStyles = xcss({
	display: 'flex',
	alignItems: 'center',
	width: '100%',
	height: '100%',
	cursor: 'pointer',
	paddingLeft: 'space.100',
	backgroundColor: 'color.background.neutral.subtle',
});

const unreadIndicatorStyles = xcss({
	position: 'absolute',
	width: token('space.075'),
	height: token('space.075'),
	border: `2px solid ${token('elevation.surface')}`,
	background: token('color.icon.information'),
	borderRadius: token('space.075'),
	boxSizing: 'content-box',
	top: '8px',
	left: '14px',
});

const refreshedIndicatorStyles = xcss({
	left: '11px',
});
